import React, { useState, useEffect } from 'react';
import styled from 'styled-components/macro';
import { IApplication, IOption, ISelection, IUser } from '../../../domain/portal-domain';
import { ModuleOption } from './module-option';
import { mainTheme } from '../../../styles/themes/main-theme';
import spinner from '../../../assets/spinner.gif';
import FreshServicesDialog from '../../nav-menu/fresh-service/fresh-service-dialog-component';
import { infoIcon } from '../../common-components/icons/icons';

interface IStyleProps {
    isLoading: boolean;
}

const MainContainer = styled.div`
    filter: ${(styles: IStyleProps) => (styles?.isLoading ? mainTheme.blur.loading : mainTheme.blur.noBlur)};
    -webkit-filter: ${(styles: IStyleProps) => (styles?.isLoading ? mainTheme.blur.loading : mainTheme.blur.noBlur)};
`;

const Spinner = styled.img`
    width: ${mainTheme.widths.loadingSpinnerWidth};
    height: ${mainTheme.heights.loadingSpinnerHeight};
`;

const LabelContainer = styled.div`
    line-height: ${mainTheme.lineHeights.h2};
    color: ${mainTheme.colors.inactiveInput};
    padding-bottom: ${mainTheme.margins.normal};
`;

const Loader = styled.div`
    position: absolute;
    z-index: 10;
    top: 10px;
    left: 40%;
`;

const SelectionContainer = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;

    @media ${mainTheme.responsiveBreakpoints.phone} {
        justify-content: center;
    }
`;

const ActiveText = styled.span`
    line-height: ${mainTheme.lineHeights.h2};
    color: ${mainTheme.colors.grey};
    padding: ${mainTheme.margins.big} 0;
`;

const InfoIconWrapper = styled.span`
    padding-right: 5px;
`;

const NoAccessText = styled.span`
    padding-top: 1px;
`;

const NoAccessMessage = styled.div`
    position: fixed;
    display: inline-flex;
    background-color: #f2f9f9;
    color: #00818a;
    border: 1px solid #00818a;
    font-size: 14px;
    padding: 6px 10px;
    border-radius: 4px;
    white-space: normal;
    text-align: left;
    max-width: 440px;
    z-index: 1000;
    pointer-events: none;
    line-height: 140.6%;

    @media ${mainTheme.responsiveBreakpoints.phone} {
        max-width: 300px;
    }
`;

export interface IModuleSelector {
    availableModules: IOption[];
    currentSelection: ISelection;
    allAppsInInstallation: IApplication[];
    user: IUser;
    isLoadingModules: boolean;
    onNotSupportedBrowser: (isNotSupported: boolean) => void;
}

export const ModuleSelector: React.FC<IModuleSelector> = ({
    user,
    availableModules,
    currentSelection,
    isLoadingModules,
    allAppsInInstallation,
    onNotSupportedBrowser,
}) => {
    const [currentlySelected, setCurrentlySelected] = useState({} as IOption);
    const [noAccessPos, setNoAccessPos] = useState<{ x: number; y: number } | null>(null);

    useEffect(() => {
        if (!noAccessPos) return;

        const controller = new AbortController();
        const dismiss = () => setNoAccessPos(null);
        const opts = (capture?: boolean) => ({ signal: controller.signal, capture });

        document.addEventListener('click', dismiss, opts());
        document.addEventListener('scroll', dismiss, opts(true));
        window.addEventListener('resize', dismiss, opts());

        return () => controller.abort();
    }, [noAccessPos]);

    const handleDisabledClick = (x: number, y: number) => {
        const isPhone = window.innerWidth <= 600;
        const messageMaxWidth = isPhone ? 300 : 440;
        const clampedX = Math.min(Math.max(x, 12), window.innerWidth - messageMaxWidth - 12);
        setNoAccessPos({ x: clampedX, y });
    };

    return (
        <>
            {isLoadingModules ? (
                <Loader>
                    <Spinner src={spinner} alt="loading..." />
                </Loader>
            ) : null}
            <FreshServicesDialog userName={user} />
            <MainContainer isLoading={isLoadingModules}>
                <LabelContainer>{availableModules.length !== 0 && <ActiveText>Select application</ActiveText>}</LabelContainer>
                <SelectionContainer>
                    {allAppsInInstallation.map((option: IApplication, index: number) => (
                        <ModuleOption
                            key={`${index}-${option.id}`}
                            appId={option.id}
                            label={option.name}
                            logoUrl={option.logoUrl}
                            availableModules={availableModules}
                            currentSelection={currentSelection}
                            selectedModule={currentlySelected}
                            onSelect={setCurrentlySelected}
                            onNotSupportedBrowser={onNotSupportedBrowser}
                            onDisabledClick={handleDisabledClick}
                        />
                    ))}
                </SelectionContainer>
            </MainContainer>
            {noAccessPos && (
                <NoAccessMessage style={{ top: noAccessPos.y + 12, left: noAccessPos.x }}>
                    <InfoIconWrapper>{infoIcon}</InfoIconWrapper>
                    <NoAccessText>You currently don't have permission to access this application.</NoAccessText>
                </NoAccessMessage>
            )}
        </>
    );
};
